import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/database';
import { TIER_ORDER, TIER_LABELS, ADMIN_ROLES, ORDER_SOURCES, RELEASE_STATUSES, CATEGORY_STATUSES, SUPPORTED_LANGUAGES } from '../../config/house.config';
import type { Tier, SupportedLang, AdminRole, ReleaseStatus, CategoryStatus, OrderSource } from '../../config/house.config';
import type { Release, ArchiveItem, HouseMessage, HouseEvent, HouseCategory, LegalDocument } from '../../types';

// Reusable admin components
const Btn = ({ children, onClick, variant = 'primary' }: { children: React.ReactNode; onClick: () => void; variant?: 'primary' | 'danger' | 'ghost' }) => {
  const cls = variant === 'primary' ? 'bg-charcoal text-ivory hover:bg-charcoal/90' : variant === 'danger' ? 'bg-burgundy text-ivory hover:bg-burgundy/80' : 'text-charcoal/40 hover:text-charcoal border border-charcoal/10';
  return <button onClick={onClick} className={`px-4 py-2 text-[10px] tracking-widest uppercase font-sans transition-colors ${cls}`}>{children}</button>;
};
const Field = ({ label, value, onChange, type = 'text', options }: { label: string; value: any; onChange: (v: any) => void; type?: string; options?: { value: string; label: string }[] }) => (
  <div className="space-y-1">
    <label className="text-[10px] tracking-widest uppercase text-charcoal/40 font-sans">{label}</label>
    {options ? (
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : type === 'textarea' ? (
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory resize-none" />
    ) : (
      <input type={type} value={value} onChange={e => onChange(type === 'number' ? Number(e.target.value) : e.target.value)} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory" />
    )}
  </div>
);
const TransFields = ({ translations, onChange, fields = ['title', 'content'] }: { translations: any; onChange: (t: any) => void; fields?: string[] }) => (
  <div className="space-y-2">
    {SUPPORTED_LANGUAGES.map(lang => (
      <div key={lang} className="border border-charcoal/5 p-3 space-y-2">
        <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">{lang.toUpperCase()}</p>
        {fields.map(f => (
          <Field key={f} label={f} value={translations?.[lang]?.[f] || ''} onChange={v => {
            const updated = { ...translations };
            if (!updated[lang]) updated[lang] = {};
            updated[lang][f] = v;
            onChange(updated);
          }} type={f === 'content' ? 'textarea' : 'text'} />
        ))}
      </div>
    ))}
  </div>
);

// Sub-pages
function MembersAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const members = store.members.filter(m => !m.deleted_at);
  const session = store.session;
  const adminId = session?.user_id || '';

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-xl">{t('admin.members')}</h2>
      <div className="space-y-2">
        {members.map(m => {
          const user = store.users.find(u => u.id === m.user_id);
          return (
            <div key={m.id} className="border border-charcoal/5 p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-serif">{m.first_name} <span className="text-[10px] text-charcoal/30 font-sans">{m.member_code}</span></p>
                  <p className="text-[10px] text-charcoal/40 font-sans">{user?.email} · {m.status} · {TIER_LABELS[m.access_tier]} · €{m.yearly_spend_eur}</p>
                  {m.black_card_candidate && <p className="text-[10px] text-gold font-sans">Black Card Candidate</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {m.status === 'waitlist' && <Btn onClick={() => store.approveMember(m.id, adminId)}>{t('admin.approve')}</Btn>}
                {m.status === 'active' && <Btn onClick={() => store.pauseMember(m.id, adminId)} variant="ghost">{t('admin.pause')}</Btn>}
                {(m.status === 'active' || m.status === 'paused') && <Btn onClick={() => store.revokeMember(m.id, adminId)} variant="danger">{t('admin.revoke')}</Btn>}
                {(m.status === 'paused' || m.status === 'revoked') && <Btn onClick={() => store.reactivateMember(m.id, adminId)}>{t('admin.reactivate')}</Btn>}
                {m.black_card_candidate && m.access_tier !== 'BLACK_CARD' && <Btn onClick={() => store.overrideTier(m.id, 'BLACK_CARD', adminId)}>Approve Black Card</Btn>}
              </div>
              <TierOverride memberId={m.id} adminId={adminId} currentTier={m.access_tier} />
              <OrderAdder memberId={m.id} adminId={adminId} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TierOverride({ memberId, adminId, currentTier }: { memberId: string; adminId: string; currentTier: Tier }) {
  const { t } = useTranslation();
  const overrideTier = useStore(s => s.overrideTier);
  const [tier, setTier] = useState(currentTier);
  return (
    <div className="flex items-center gap-2 pt-1">
      <select value={tier} onChange={e => setTier(e.target.value as Tier)} className="border border-charcoal/10 p-1 text-[10px] font-sans bg-ivory">
        {TIER_ORDER.map(t => <option key={t} value={t}>{TIER_LABELS[t]}</option>)}
      </select>
      <Btn onClick={() => overrideTier(memberId, tier, adminId)} variant="ghost">{t('admin.overrideTier')}</Btn>
    </div>
  );
}

function OrderAdder({ memberId, adminId }: { memberId: string; adminId: string }) {
  const { t } = useTranslation();
  const store = useStore();
  const [amount, setAmount] = useState(0);
  const [source, setSource] = useState<OrderSource>('manual_admin');
  const handleAdd = () => {
    if (amount <= 0) return;
    const order = store.createOrder({ member_id: memberId, amount_eur: amount, source }, adminId);
    store.payOrder(order.id, adminId);
    setAmount(0);
  };
  return (
    <div className="flex items-center gap-2 pt-1">
      <input type="number" value={amount} onChange={e => setAmount(Number(e.target.value))} placeholder="EUR" className="border border-charcoal/10 p-1 w-20 text-[10px] font-sans bg-ivory" />
      <select value={source} onChange={e => setSource(e.target.value as OrderSource)} className="border border-charcoal/10 p-1 text-[10px] font-sans bg-ivory">
        {ORDER_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <Btn onClick={handleAdd} variant="ghost">{t('admin.addOrder')}</Btn>
    </div>
  );
}

function ReleasesAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const items = store.releases.filter(r => !r.deleted_at);
  const adminId = store.session?.user_id || '';
  const [editing, setEditing] = useState<Partial<Release> | null>(null);

  const save = () => {
    if (!editing) return;
    store.upsertRelease(editing, adminId);
    setEditing(null);
  };

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl">{editing.id ? t('admin.edit') : t('admin.create')}</h2>
          <Btn onClick={() => setEditing(null)} variant="ghost">{t('admin.back')}</Btn>
        </div>
        <Field label={t('admin.slug')} value={editing.slug || ''} onChange={v => setEditing({ ...editing, slug: v })} />
        <Field label={t('admin.status')} value={editing.status || 'upcoming'} onChange={v => setEditing({ ...editing, status: v })} options={RELEASE_STATUSES.map(s => ({ value: s, label: s }))} />
        <Field label={t('admin.category')} value={editing.house_category_slug || ''} onChange={v => setEditing({ ...editing, house_category_slug: v })} options={store.house_categories.filter(c => !c.deleted_at && c.status === 'active').map(c => ({ value: c.slug, label: c.slug }))} />
        <TransFields translations={editing.translations || {}} onChange={v => setEditing({ ...editing, translations: v })} />
        <Btn onClick={save}>{t('admin.save')}</Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl">{t('admin.releases')}</h2>
        <Btn onClick={() => setEditing({})}>{t('admin.create')}</Btn>
      </div>
      {items.map(r => (
        <div key={r.id} className="border border-charcoal/5 p-3 flex items-center justify-between">
          <div>
            <p className="font-serif text-sm">{r.translations.en.title}</p>
            <p className="text-[10px] text-charcoal/30 font-sans">{r.slug} · {r.status} · {r.house_category_slug}</p>
          </div>
          <div className="flex gap-2">
            <Btn onClick={() => setEditing(r)} variant="ghost">{t('admin.edit')}</Btn>
            <Btn onClick={() => store.deleteRelease(r.id, adminId)} variant="danger">{t('admin.delete')}</Btn>
          </div>
        </div>
      ))}
    </div>
  );
}

function ArchiveAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const items = store.archive_items.filter(a => !a.deleted_at);
  const adminId = store.session?.user_id || '';
  const [editing, setEditing] = useState<Partial<ArchiveItem> | null>(null);

  const save = () => { if (!editing) return; store.upsertArchiveItem(editing, adminId); setEditing(null); };

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{editing.id ? t('admin.edit') : t('admin.create')}</h2><Btn onClick={() => setEditing(null)} variant="ghost">{t('admin.back')}</Btn></div>
        <Field label={t('admin.slug')} value={editing.slug || ''} onChange={v => setEditing({ ...editing, slug: v })} />
        <Field label={t('admin.category')} value={editing.house_category_slug || ''} onChange={v => setEditing({ ...editing, house_category_slug: v })} options={store.house_categories.filter(c => !c.deleted_at && c.status === 'active').map(c => ({ value: c.slug, label: c.slug }))} />
        <Field label={t('admin.minTier')} value={editing.min_tier || 'INITIATE'} onChange={v => setEditing({ ...editing, min_tier: v })} options={TIER_ORDER.map(t => ({ value: t, label: TIER_LABELS[t] }))} />
        <TransFields translations={editing.translations || {}} onChange={v => setEditing({ ...editing, translations: v })} />
        <Btn onClick={save}>{t('admin.save')}</Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{t('admin.archive')}</h2><Btn onClick={() => setEditing({})}>{t('admin.create')}</Btn></div>
      {items.map(a => (
        <div key={a.id} className="border border-charcoal/5 p-3 flex items-center justify-between">
          <div><p className="font-serif text-sm">{a.translations.en.title}</p><p className="text-[10px] text-charcoal/30 font-sans">{a.slug} · {a.house_category_slug} · {TIER_LABELS[a.min_tier]}</p></div>
          <div className="flex gap-2"><Btn onClick={() => setEditing(a)} variant="ghost">{t('admin.edit')}</Btn><Btn onClick={() => store.deleteArchiveItem(a.id, adminId)} variant="danger">{t('admin.delete')}</Btn></div>
        </div>
      ))}
    </div>
  );
}

function MessagesAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const items = store.house_messages.filter(m => !m.deleted_at);
  const adminId = store.session?.user_id || '';
  const [editing, setEditing] = useState<Partial<HouseMessage> | null>(null);

  const save = () => { if (!editing) return; store.upsertMessage(editing, adminId); setEditing(null); };
  const togglePub = (m: HouseMessage) => { store.upsertMessage({ ...m, is_published: !m.is_published, published_at: !m.is_published ? new Date().toISOString() : null }, adminId); };

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{editing.id ? t('admin.edit') : t('admin.create')}</h2><Btn onClick={() => setEditing(null)} variant="ghost">{t('admin.back')}</Btn></div>
        <Field label={t('admin.slug')} value={editing.slug || ''} onChange={v => setEditing({ ...editing, slug: v })} />
        <TransFields translations={editing.translations || {}} onChange={v => setEditing({ ...editing, translations: v })} />
        <Btn onClick={save}>{t('admin.save')}</Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{t('admin.messages')}</h2><Btn onClick={() => setEditing({})}>{t('admin.create')}</Btn></div>
      {items.map(m => (
        <div key={m.id} className="border border-charcoal/5 p-3 flex items-center justify-between">
          <div><p className="font-serif text-sm">{m.translations.en.title}</p><p className="text-[10px] text-charcoal/30 font-sans">{m.slug} · {m.is_published ? 'Published' : 'Draft'}</p></div>
          <div className="flex gap-2"><Btn onClick={() => togglePub(m)} variant="ghost">{m.is_published ? t('admin.unpublish') : t('admin.publish')}</Btn><Btn onClick={() => setEditing(m)} variant="ghost">{t('admin.edit')}</Btn><Btn onClick={() => store.deleteMessage(m.id, adminId)} variant="danger">{t('admin.delete')}</Btn></div>
        </div>
      ))}
    </div>
  );
}

function EventsAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const items = store.events.filter(e => !e.deleted_at);
  const adminId = store.session?.user_id || '';
  const [editing, setEditing] = useState<Partial<HouseEvent> | null>(null);

  const save = () => { if (!editing) return; store.upsertEvent(editing, adminId); setEditing(null); };

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{editing.id ? t('admin.edit') : t('admin.create')}</h2><Btn onClick={() => setEditing(null)} variant="ghost">{t('admin.back')}</Btn></div>
        <Field label={t('admin.slug')} value={editing.slug || ''} onChange={v => setEditing({ ...editing, slug: v })} />
        <Field label={t('admin.eventDate')} value={editing.event_date?.substring(0, 10) || ''} onChange={v => setEditing({ ...editing, event_date: v })} type="date" />
        <Field label={t('admin.location')} value={editing.location || ''} onChange={v => setEditing({ ...editing, location: v })} />
        <Field label={t('admin.minTier')} value={editing.min_tier || 'INITIATE'} onChange={v => setEditing({ ...editing, min_tier: v })} options={TIER_ORDER.map(t => ({ value: t, label: TIER_LABELS[t] }))} />
        <TransFields translations={editing.translations || {}} onChange={v => setEditing({ ...editing, translations: v })} />
        <Btn onClick={save}>{t('admin.save')}</Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{t('admin.events')}</h2><Btn onClick={() => setEditing({})}>{t('admin.create')}</Btn></div>
      {items.map(e => (
        <div key={e.id} className="border border-charcoal/5 p-3 flex items-center justify-between">
          <div><p className="font-serif text-sm">{e.translations.en.title}</p><p className="text-[10px] text-charcoal/30 font-sans">{e.slug} · {new Date(e.event_date).toLocaleDateString()} · {e.location}</p></div>
          <div className="flex gap-2"><Btn onClick={() => setEditing(e)} variant="ghost">{t('admin.edit')}</Btn><Btn onClick={() => store.deleteEvent(e.id, adminId)} variant="danger">{t('admin.delete')}</Btn></div>
        </div>
      ))}
    </div>
  );
}

function InvitesAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const invites = store.invites.filter(i => !i.deleted_at);
  return (
    <div className="space-y-4">
      <h2 className="font-serif text-xl">{t('admin.invites')}</h2>
      {invites.length === 0 && <p className="text-sm text-charcoal/50 font-sans">{t('admin.noResults')}</p>}
      {invites.map(i => {
        const issuer = store.members.find(m => m.id === i.issued_by_member_id);
        return (
          <div key={i.id} className="border border-charcoal/5 p-3">
            <p className="font-mono text-sm">{i.code}</p>
            <p className="text-[10px] text-charcoal/30 font-sans">By: {issuer?.first_name || 'Unknown'} · {i.status} · {i.used_count}/{i.max_uses} · Exp: {new Date(i.expires_at).toLocaleDateString()}</p>
          </div>
        );
      })}
    </div>
  );
}

function CategoriesAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const items = store.house_categories.filter(c => !c.deleted_at);
  const adminId = store.session?.user_id || '';
  const [editing, setEditing] = useState<Partial<HouseCategory> | null>(null);

  const save = () => { if (!editing) return; store.upsertCategory(editing, adminId); setEditing(null); };

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{editing.id ? t('admin.edit') : t('admin.create')}</h2><Btn onClick={() => setEditing(null)} variant="ghost">{t('admin.back')}</Btn></div>
        <Field label={t('admin.slug')} value={editing.slug || ''} onChange={v => setEditing({ ...editing, slug: v })} />
        <Field label={t('admin.status')} value={editing.status || 'active'} onChange={v => setEditing({ ...editing, status: v })} options={CATEGORY_STATUSES.map(s => ({ value: s, label: s }))} />
        <Field label={t('admin.sortOrder')} value={editing.sort_order || 0} onChange={v => setEditing({ ...editing, sort_order: Number(v) })} type="number" />
        <TransFields translations={editing.translations || {}} onChange={v => setEditing({ ...editing, translations: v })} fields={['name']} />
        <Btn onClick={save}>{t('admin.save')}</Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{t('admin.categories')}</h2><Btn onClick={() => setEditing({})}>{t('admin.create')}</Btn></div>
      {items.sort((a, b) => a.sort_order - b.sort_order).map(c => (
        <div key={c.id} className="border border-charcoal/5 p-3 flex items-center justify-between">
          <div><p className="font-serif text-sm">{c.slug}</p><p className="text-[10px] text-charcoal/30 font-sans">{c.status} · Order: {c.sort_order}</p></div>
          <Btn onClick={() => setEditing(c)} variant="ghost">{t('admin.edit')}</Btn>
        </div>
      ))}
    </div>
  );
}

function LegalAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const docs = store.legal_documents;
  const adminId = store.session?.user_id || '';
  const [editing, setEditing] = useState<Partial<LegalDocument> | null>(null);

  const save = () => { if (!editing) return; store.upsertLegalDoc(editing, adminId); setEditing(null); };

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{editing.id ? t('admin.edit') : t('admin.create')}</h2><Btn onClick={() => setEditing(null)} variant="ghost">{t('admin.back')}</Btn></div>
        <Field label={t('admin.documentType')} value={editing.document_type || 'terms'} onChange={v => setEditing({ ...editing, document_type: v })} options={[{ value: 'terms', label: 'Terms' }, { value: 'privacy', label: 'Privacy' }, { value: 'cookies', label: 'Cookies' }]} />
        <Field label={t('admin.version')} value={editing.version || ''} onChange={v => setEditing({ ...editing, version: v })} />
        <Field label={t('admin.contentUrl')} value={editing.content_url || ''} onChange={v => setEditing({ ...editing, content_url: v })} />
        <Btn onClick={save}>{t('admin.save')}</Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><h2 className="font-serif text-xl">{t('admin.legal')}</h2><Btn onClick={() => setEditing({})}>{t('admin.create')}</Btn></div>
      {docs.map(d => (
        <div key={d.id} className="border border-charcoal/5 p-3 flex items-center justify-between">
          <div><p className="font-serif text-sm">{d.document_type} v{d.version}</p><p className="text-[10px] text-charcoal/30 font-sans">{d.is_active ? 'Active' : 'Inactive'} · {new Date(d.effective_at).toLocaleDateString()}</p></div>
          <Btn onClick={() => setEditing(d)} variant="ghost">{t('admin.edit')}</Btn>
        </div>
      ))}
    </div>
  );
}

function AuditAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const logs = store.admin_audit_log.slice().reverse().slice(0, 50);
  return (
    <div className="space-y-4">
      <h2 className="font-serif text-xl">{t('admin.audit')}</h2>
      {logs.length === 0 && <p className="text-sm text-charcoal/50 font-sans">{t('admin.noResults')}</p>}
      <div className="space-y-1">
        {logs.map(l => (
          <div key={l.id} className="border-b border-charcoal/5 py-2 text-[11px] font-sans text-charcoal/60">
            <span className="text-charcoal/30">{new Date(l.created_at).toLocaleString()}</span> · <span className="font-medium text-charcoal">{l.action}</span> · {l.target_type}:{l.target_id.substring(0, 8)}
          </div>
        ))}
      </div>
    </div>
  );
}

function HealthAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const sysEvents = store.system_events.slice().reverse().slice(0, 30);
  const failedJobs = store.failed_jobs.slice().reverse().slice(0, 10);
  return (
    <div className="space-y-6">
      <h2 className="font-serif text-xl">{t('admin.health')}</h2>
      <div>
        <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans mb-2">System Events ({store.system_events.length})</p>
        <div className="space-y-1">
          {sysEvents.map(e => (
            <div key={e.id} className="border-b border-charcoal/5 py-1 text-[11px] font-sans text-charcoal/60">
              <span className="text-charcoal/30">{new Date(e.created_at).toLocaleString()}</span> · {e.event_type}
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans mb-2">Failed Jobs ({store.failed_jobs.length})</p>
        {failedJobs.length === 0 ? <p className="text-sm text-charcoal/50 font-sans">No failed jobs.</p> : failedJobs.map(j => (
          <div key={j.id} className="border-b border-charcoal/5 py-1 text-[11px] font-sans text-charcoal/60">
            {j.job_type}: {j.error_message}
          </div>
        ))}
      </div>
      <div>
        <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans mb-2">DB Stats</p>
        <div className="grid grid-cols-2 gap-2 text-[11px] font-sans text-charcoal/50">
          <span>Users: {store.users.filter(u => !u.deleted_at).length}</span>
          <span>Members: {store.members.filter(m => !m.deleted_at).length}</span>
          <span>Orders: {store.orders.filter(o => !o.deleted_at).length}</span>
          <span>Releases: {store.releases.filter(r => !r.deleted_at).length}</span>
          <span>Archive: {store.archive_items.filter(a => !a.deleted_at).length}</span>
          <span>Messages: {store.house_messages.filter(m => !m.deleted_at).length}</span>
          <span>Events: {store.events.filter(e => !e.deleted_at).length}</span>
          <span>Invites: {store.invites.filter(i => !i.deleted_at).length}</span>
        </div>
      </div>
    </div>
  );
}

// Router for admin sub-pages
const ADMIN_PAGES: Record<string, React.FC> = {
  members: MembersAdmin,
  releases: ReleasesAdmin,
  archive: ArchiveAdmin,
  messages: MessagesAdmin,
  events: EventsAdmin,
  invites: InvitesAdmin,
  categories: CategoriesAdmin,
  legal: LegalAdmin,
  audit: AuditAdmin,
  health: HealthAdmin,
};

export { MembersAdmin, ReleasesAdmin, ArchiveAdmin, MessagesAdmin, EventsAdmin, InvitesAdmin, CategoriesAdmin, LegalAdmin, AuditAdmin, HealthAdmin };

export default function AdminDashboard() {
  const { t } = useTranslation();
  const store = useStore();
  const waitlistCount = store.members.filter(m => m.status === 'waitlist' && !m.deleted_at).length;
  const activeCount = store.members.filter(m => m.status === 'active' && !m.deleted_at).length;
  const totalOrders = store.orders.filter(o => !o.deleted_at).length;
  const recentAudit = store.admin_audit_log.slice(-3).reverse();

  return (
    <div className="space-y-8">
      <h1 className="font-serif text-2xl tracking-widest">{t('admin.title')}</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-charcoal/5 p-4 text-center">
          <p className="font-serif text-2xl">{waitlistCount}</p>
          <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Waitlist</p>
        </div>
        <div className="border border-charcoal/5 p-4 text-center">
          <p className="font-serif text-2xl">{activeCount}</p>
          <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Active</p>
        </div>
        <div className="border border-charcoal/5 p-4 text-center">
          <p className="font-serif text-2xl">{totalOrders}</p>
          <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Orders</p>
        </div>
        <div className="border border-charcoal/5 p-4 text-center">
          <p className="font-serif text-2xl">{store.system_events.length}</p>
          <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Events</p>
        </div>
      </div>

      {recentAudit.length > 0 && (
        <div>
          <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans mb-2">Recent Activity</p>
          {recentAudit.map(l => (
            <p key={l.id} className="text-[11px] font-sans text-charcoal/50 py-1 border-b border-charcoal/5">{l.action} · {l.target_type} · {new Date(l.created_at).toLocaleString()}</p>
          ))}
        </div>
      )}
    </div>
  );
}
